import { RoomsService as RoomsApi } from '../../shared/api/generated/rooms/rooms.service';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { User } from '../../state/user.repository';
import { BanEntry, listBansResponseSchema } from '../../shared/schemas/backend-api';

/**
 * Talks to the andor-server moderation endpoints — kicking participants and
 * managing domain-wide bans. Every call is gated behind the `kickUser`
 * permission server-side.
 *
 * Deliberately a pure app-server HTTP service with no dependency on the ODIN
 * runtime: callers supply the `roomId` (the live room to evict from) when they
 * have one. This keeps it usable from contexts with no live connection — e.g. a
 * future admin interface banning by `userId` alone.
 */
@Injectable({ providedIn: 'root' })
export class ModerationService {
  private readonly roomsApi = inject(RoomsApi);

  /**
   * Kick a participant, optionally banning them domain-wide for
   * `banDurationSeconds`. `roomId` (the live room to evict from) is the target
   * for future live eviction — pass the actually-joined room when acting from
   * one; omit it for room-less calls. Live eviction is not implemented yet: the
   * server logs the kick and, when a duration is given, records a domain-wide
   * ban that blocks the user's next join.
   */
  async kickParticipant(
    user: User,
    opts: { roomId?: string; banDurationSeconds?: number } = {}
  ): Promise<void> {
    const body: {
      userId: string;
      userName: string;
      roomId?: string;
      banDurationSeconds?: number;
    } = {
      userId: user.id,
      userName: user.data.name,
    };
    if (opts.roomId) {
      body.roomId = opts.roomId;
    }
    if (opts.banDurationSeconds !== undefined) {
      body.banDurationSeconds = opts.banDurationSeconds;
    }
    try {
      await firstValueFrom(this.roomsApi.kickUser(body));
    } catch (e) {
      console.error('Failed to kick participant', e);
    }
  }

  /**
   * Fetch the domain-wide ban list. Returns an empty list on failure.
   */
  async listBans(): Promise<BanEntry[]> {
    try {
      const response = await firstValueFrom(this.roomsApi.listBans());
      return listBansResponseSchema.parse(response).bans;
    } catch (e) {
      console.error('Failed to list bans', e);
      return [];
    }
  }

  /**
   * Lift a user's domain-wide ban early. Returns whether the ban was removed.
   */
  async removeBan(userId: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.roomsApi.removeBan({
          userId,
        })
      );
      return response?.success === true;
    } catch (e) {
      console.error('Failed to remove ban', e);
      return false;
    }
  }
}
